import { useAppMode } from "@/hooks/use-app-mode";
import { Suspense } from "react";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { AppShell } from "@/components/layout/AppShell";
import { RequireAuth, RoleGate } from "@/components/layout/Guards";
import { PageLoader } from "@/components/common/Page";
import { RouteError } from "@/components/common/RouteError";
import { VersionBanner } from "@/components/common/VersionBanner";
import { lazyRetry } from "@/lib/lazyRetry";
import "@/App.css";

const Login = lazyRetry(() => import("@/pages/auth/Login"));
const ForgotPassword = lazyRetry(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("@/pages/auth/ResetPassword"));
const Dashboard = lazyRetry(() => import("@/pages/Dashboard"));
const Cockpit = lazyRetry(() => import("@/pages/Cockpit"));
const Notifications = lazyRetry(() => import("@/pages/Notifications"));
const Profile = lazyRetry(() => import("@/pages/Profile"));
const NotFound = lazyRetry(() => import("@/pages/NotFound"));
const UsersList = lazyRetry(() => import("@/pages/admin/UsersList"));
const MagentoOrders = lazyRetry(() => import("@/pages/admin/MagentoOrders"));
const MagentoOrderDetail = lazyRetry(() => import("@/pages/admin/MagentoOrderDetail"));
const UserForm = lazyRetry(() => import("@/pages/admin/UserForm"));
const TeamsList = lazyRetry(() => import("@/pages/admin/TeamsList"));
const TeamForm = lazyRetry(() => import("@/pages/admin/TeamForm"));
const RolesPermissions = lazyRetry(() => import("@/pages/admin/RolesPermissions"));
const BrandsList = lazyRetry(() => import("@/pages/admin/BrandsList"));
const BrandForm = lazyRetry(() => import("@/pages/admin/BrandForm"));
const Mailboxes = lazyRetry(() => import("@/pages/admin/Mailboxes"));
const EmailTemplates = lazyRetry(() => import("@/pages/admin/EmailTemplates"));
const EmailTemplateForm = lazyRetry(() => import("@/pages/admin/EmailTemplateForm"));
const Statuses = lazyRetry(() => import("@/pages/admin/Statuses"));
const StatusForm = lazyRetry(() => import("@/pages/admin/StatusForm"));
const Categories = lazyRetry(() => import("@/pages/admin/Categories"));
const AuditLog = lazyRetry(() => import("@/pages/admin/AuditLog"));
const Outbox = lazyRetry(() => import("@/pages/admin/Outbox"));
const CalendarPage = lazyRetry(() => import("@/pages/calendar/CalendarPage"));
const ExceptionForm = lazyRetry(() => import("@/pages/calendar/ExceptionForm"));
const BaseHours = lazyRetry(() => import("@/pages/calendar/BaseHours"));
const BankHolidays = lazyRetry(() => import("@/pages/calendar/BankHolidays"));
const SettingsIndex = lazyRetry(() => import("@/pages/settings/SettingsIndex"));
const AppStyling = lazyRetry(() => import("@/pages/settings/AppStyling"));
const SecuritySettings = lazyRetry(() => import("@/pages/settings/SecuritySettings"));
const EmailSettings = lazyRetry(() => import("@/pages/settings/EmailSettings"));
const MagentoSettings = lazyRetry(() => import("@/pages/settings/MagentoSettings"));
const SearchPage = lazyRetry(() => import("@/pages/SearchPage"));
const Reports = lazyRetry(() => import("@/pages/Reports"));
const RulesList = lazyRetry(() => import("@/pages/settings/RulesList"));
const RuleForm = lazyRetry(() => import("@/pages/settings/RuleForm"));
const SlaPolicies = lazyRetry(() => import("@/pages/settings/SlaPolicies"));
const SlaPolicyForm = lazyRetry(() => import("@/pages/settings/SlaPolicyForm"));
const Health = lazyRetry(() => import("@/pages/admin/Health"));
const Retention = lazyRetry(() => import("@/pages/admin/Retention"));
const Gdpr = lazyRetry(() => import("@/pages/admin/Gdpr"));
const NotificationDefaults = lazyRetry(() => import("@/pages/settings/NotificationDefaults"));
const TicketsList = lazyRetry(() => import("@/pages/tickets/TicketsList"));
const TicketDetail = lazyRetry(() => import("@/pages/tickets/TicketDetail"));
const ContactsList = lazyRetry(() => import("@/pages/contacts/Contacts"));
const ContactMerge = lazyRetry(() => import("@/pages/contacts/ContactMerge"));
const ContactDetail = lazyRetry(() => import("@/pages/contacts/ContactDetail"));
const EmailSimulator = lazyRetry(() => import("@/pages/admin/EmailSimulator"));
const PhoneSimulator = lazyRetry(() => import("@/pages/phone/PhoneSimulator"));
const Readiness = lazyRetry(() => import("@/pages/admin/Readiness"));
const TestData = lazyRetry(() => import("@/pages/admin/TestData"));
const PhoneSettings = lazyRetry(() => import("@/pages/settings/PhoneSettings"));
const UnloggedCalls = lazyRetry(() => import("@/pages/phone/UnloggedCalls"));
const NewTicket = lazyRetry(() => import("@/pages/tickets/NewTicket"));
const TicketAction = lazyRetry(() => import("@/pages/tickets/TicketAction"));
const Templates = lazyRetry(() => import("@/pages/templates/Templates"));
const TemplateForm = lazyRetry(() => import("@/pages/templates/TemplateForm"));
const CustomerTicket = lazyRetry(() => import("@/pages/public/CustomerTicket"));
const SuppliersList = lazyRetry(() => import("@/pages/suppliers/SuppliersList"));
const SupplierForm = lazyRetry(() => import("@/pages/suppliers/SupplierForm"));
const SupplierDetail = lazyRetry(() => import("@/pages/suppliers/SupplierDetail"));
const UserDetail = lazyRetry(() => import("@/pages/admin/UserDetail"));

const S = (el) => <Suspense fallback={<PageLoader />}>{el}</Suspense>;
const ADMIN = ["admin"];
const STAFF = ["admin", "manager", "agent"];
const PreviewOnly = ({ children }) => {
  const mode = useAppMode();
  if (!mode) return null;
  return mode === "preview" ? children : <NotFound />;
};
const previewPage = (path, Comp, roles) => page(path, () => <PreviewOnly><Comp /></PreviewOnly>, roles);
const page = (path, Comp, roles) => ({ path, element: S(<RoleGate roles={roles}><Comp /></RoleGate>), errorElement: <RouteError /> });

const router = createBrowserRouter([
  { path: "/login", element: S(<Login />), errorElement: <RouteError /> },
  { path: "/forgot-password", element: S(<ForgotPassword />), errorElement: <RouteError /> },
  { path: "/reset-password", element: S(<ResetPassword />), errorElement: <RouteError /> },
  { path: "/t/:token", element: S(<CustomerTicket />), errorElement: <RouteError /> },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/cockpit" replace /> },
      page("cockpit", Cockpit),
      page("dashboard", Dashboard, ADMIN),
      page("notifications", Notifications),
      page("profile", Profile),
      page("calendar", CalendarPage),
      page("calendar/exceptions/new", ExceptionForm, ["admin", "manager"]),
      page("calendar/exceptions/:id/edit", ExceptionForm, ["admin", "manager"]),
      page("admin/base-hours", BaseHours, ADMIN),
      page("admin/bank-holidays", BankHolidays, ADMIN),
      page("admin/users", UsersList, ADMIN),
      page("admin/magento-orders", MagentoOrders, ADMIN),
      page("admin/magento-orders/:id", MagentoOrderDetail, ADMIN),
      page("admin/users/new", UserForm, ADMIN),
      page("admin/users/:id/edit", UserForm, ADMIN),
      page("admin/users/:id", UserDetail, ADMIN),
      page("admin/teams", TeamsList, ADMIN),
      page("admin/teams/new", TeamForm, ADMIN),
      page("admin/teams/:id/edit", TeamForm, ADMIN),
      page("admin/roles", RolesPermissions, ADMIN),
      page("admin/brands", BrandsList, ADMIN),
      page("admin/email-templates", EmailTemplates, ["admin", "manager"]),
      page("admin/email-templates/new", EmailTemplateForm, ["admin", "manager"]),
      page("admin/email-templates/:id", EmailTemplateForm, ["admin", "manager"]),
      page("admin/brands/new", BrandForm, ADMIN),
      page("admin/brands/:id/edit", BrandForm, ADMIN),
      page("admin/mailboxes", Mailboxes, ADMIN),
      page("admin/statuses", Statuses, ADMIN),
      page("admin/statuses/new", StatusForm, ADMIN),
      page("admin/statuses/:id/edit", StatusForm, ADMIN),
      page("admin/categories", Categories, ADMIN),
      page("admin/audit", AuditLog, ADMIN),
      page("admin/outbox", Outbox, ADMIN),
      page("settings", SettingsIndex, ADMIN),
      page("settings/app-styling", AppStyling, ADMIN),
      page("settings/security", SecuritySettings, ADMIN),
      page("settings/email", EmailSettings, ADMIN),
      page("settings/magento", MagentoSettings, ADMIN),
      page("settings/notification-defaults", NotificationDefaults, ADMIN),
      page("search", SearchPage),
      page("reports", Reports, ["admin", "manager", "readonly"]),
      page("settings/rules", RulesList, ["admin", "manager"]),
      page("settings/rules/:ruleId", RuleForm, ["admin", "manager"]),
      page("settings/rules/:ruleId/edit", RuleForm, ["admin", "manager"]),
      page("settings/sla", SlaPolicies, ADMIN),
      page("settings/sla/:brandId", SlaPolicyForm, ADMIN),
      page("admin/health", Health, ADMIN),
      page("admin/retention", Retention, ADMIN),
      page("admin/gdpr", Gdpr, ADMIN),
      page("tickets", TicketsList),
      page("tickets/new", NewTicket, STAFF),
      page("tickets/:id", TicketDetail),
      page("tickets/:id/:action", TicketAction, STAFF),
      page("templates", Templates, STAFF),
      page("templates/new", TemplateForm, STAFF),
      page("templates/:id/edit", TemplateForm, STAFF),
      page("contacts", ContactsList),
      page("suppliers", SuppliersList),
      page("suppliers/new", SupplierForm),
      page("suppliers/:supplierId", SupplierDetail),
      page("suppliers/:supplierId/edit", SupplierForm),
      page("contacts/:id", ContactDetail),
      page("contacts/:id/merge", ContactMerge, STAFF),
      previewPage("admin/email-simulator", EmailSimulator, ADMIN),
      previewPage("admin/phone-simulator", PhoneSimulator, ADMIN),
      previewPage("admin/readiness", Readiness, ADMIN),
      previewPage("admin/test-data", TestData, ADMIN),
      page("settings/phone", PhoneSettings, ADMIN),
      page("calls/unlogged", UnloggedCalls),
      { path: "*", element: S(<NotFound />), errorElement: <RouteError /> },
    ],
  },
]);

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <BrandingProvider>
        <AuthProvider>
          <RouterProvider router={router} />
          <VersionBanner />
          <Toaster position="bottom-right" richColors closeButton />
        </AuthProvider>
      </BrandingProvider>
    </ThemeProvider>
  );
}
